import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { CircularProgress, Box, Snackbar, Alert, Modal, Button, Switch, FormControlLabel } from "@mui/material";
import DynamicTable from "../../../components/DynamicTable";
import { getFilePreviewUrl, getDownloadUrl, getFileName } from "../../../utils/fileHelpers";

const API_URL = process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec';

const PoliticaCard = ({ politica, index, updatePolitica, preguntas, setPreguntas, handleFileChange, handlePreviewFile, toggleActivo }) => {
  const tituloRef = useRef(null);
  const descripcionRef = useRef(null);

  const [localTitulo, setLocalTitulo] = useState(politica.titulo || "");
  const [localDescripcion, setLocalDescripcion] = useState(politica.descripcion || "");
  const [localRequiereArchivo, setLocalRequiereArchivo] = useState(politica.requiere_archivo === true);

  useEffect(() => {
    setLocalTitulo(politica.titulo || "");
    setLocalDescripcion(politica.descripcion || "");
    setLocalRequiereArchivo(politica.requiere_archivo === true);
  }, [politica.titulo, politica.descripcion, politica.requiere_archivo]);

  const handleTituloChange = (e) => {
    const value = e.target.value;
    setLocalTitulo(value);
    setTimeout(() => {
      updatePolitica(index, "titulo", value);
    }, 50);
  };

  const handleDescripcionChange = (e) => {
    const value = e.target.value;
    setLocalDescripcion(value);
    setTimeout(() => {
      updatePolitica(index, "descripcion", value);
    }, 50);
  };

  const handleRequiereArchivoChange = (e) => {
    const checked = e.target.checked;
    console.log(`Checkbox cambiado para política ${politica.titulo}: ${checked}`);
    setLocalRequiereArchivo(checked);
    updatePolitica(index, "requiere_archivo", checked);
  };

  const createNewRow = () => ({
    id: `new-${Date.now()}`,
    titulo: "",
    descripcion: "",
    tipo_persona: "natural",
    tipo: "certificado",
    obligatorio: true,
    requiere_archivo: false,
    orden: preguntas.length + 1,
    archivo: null,
    ruta_archivo: null,
    activo: true,
  });

  return (
    <div className="card mb-3 shadow-sm">
      <div className="card-body">
        <div className="row g-2">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div className="flex-grow-1 me-2">
                <input
                  ref={tituloRef}
                  type="text"
                  className="form-control"
                  value={localTitulo}
                  onChange={handleTituloChange}
                  placeholder="Título de la política"
                />
              </div>
              <span className="badge bg-success text-capitalize">Activo</span>
            </div>
          </div>

          <div className="col-12">
            <label className="form-label fw-semibold">Descripción</label>
            <textarea
              ref={descripcionRef}
              className="form-control"
              rows="2"
              value={localDescripcion}
              onChange={handleDescripcionChange}
              placeholder="Descripción de la política..."
            />
          </div>

          <div className="col-12 col-sm-6">
            <label className="form-label fw-semibold">Tipo de Persona</label>
            <select
              className="form-select"
              value={politica.tipo_persona || ""}
              onChange={(e) => updatePolitica(index, "tipo_persona", e.target.value)}
            >
              <option value="natural">Persona Natural</option>
              <option value="juridica">Persona Jurídica</option>
            </select>
          </div>

          <div className="col-12 col-sm-6">
            <label className="form-label fw-semibold">Obligatoriedad</label>
            <select
              className="form-select"
              value={politica.obligatorio ? "obligatorio" : "opcional"}
              onChange={(e) =>
                updatePolitica(index, "obligatorio", e.target.value === "obligatorio")
              }
            >
              <option value="obligatorio">Obligatorio</option>
              <option value="opcional">Opcional</option>
            </select>
          </div>

          <div className="col-12">
            <FormControlLabel
              control={
                <Switch
                  checked={localRequiereArchivo}
                  onChange={handleRequiereArchivoChange}
                  color="primary"
                />
              }
              label="Requiere que el proveedor suba un documento"
            />
          </div>

          <div className="col-12">
            <label className="form-label fw-semibold">Archivo de Política</label>
            <div className="d-flex flex-column gap-2">
              <input
                type="file"
                accept=".pdf"
                id={`mobile-file-input-${politica.id}`}
                className="form-control d-none"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileChange(index, file);
                  }
                  e.target.value = "";
                }}
              />

              <div className="d-flex gap-2 align-items-center">
                <label
                  htmlFor={`mobile-file-input-${politica.id}`}
                  className="btn btn-outline-primary flex-grow-1"
                >
                  <i className="bi bi-upload me-1"></i>
                  {politica.archivo?.name || getFileName(politica) || "Seleccionar archivo"}
                </label>

                {(politica.archivo || politica.ruta_archivo) && (
                  <button
                    className="btn btn-outline-info"
                    onClick={() => handlePreviewFile(politica)}
                    title="Previsualizar archivo"
                    type="button"
                  >
                    <i className="bi bi-eye"></i>
                  </button>
                )}
              </div>

              {politica.archivo && (
                <div className="alert alert-info py-1 mb-0">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    Nuevo archivo seleccionado: {politica.archivo.name}
                  </small>
                </div>
              )}

              {politica.ruta_archivo && !politica.archivo && (
                <div className="alert alert-success py-1 mb-0">
                  <small>
                    <i className="bi bi-check-circle me-1"></i>
                    Archivo ya cargado: {getFileName(politica)}
                  </small>
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center pt-2 border-top">
              <small className="text-muted">Orden: {politica.orden}</small>
              <div className="d-flex gap-1">
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => {
                    const newRow = createNewRow();
                    newRow.orden = preguntas.length + 1;
                    setPreguntas([...preguntas, newRow]);
                  }}
                  title="Agregar nueva política"
                  type="button"
                >
                  <i className="bi bi-plus-lg"></i>
                </button>

                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => toggleActivo(politica.id, false)}
                  title="Desactivar política"
                  type="button"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Politicas = () => {
  const [preguntas, setPreguntas] = useState([]);
  const [preguntasInactivas, setPreguntasInactivas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tipoPersonaFiltro, setTipoPersonaFiltro] = useState("todos");
  const [tituloFiltro, setTituloFiltro] = useState("");
  const [requiereArchivoFiltro, setRequiereArchivoFiltro] = useState("todos");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState({
    url: null,
    fileName: null,
    downloadUrl: null,
  });

  const createNewRow = () => ({
    id: `new-${Date.now()}`,
    titulo: "",
    descripcion: "",
    tipo_persona: "natural",
    tipo: "certificado",
    obligatorio: true,
    requiere_archivo: false,
    orden: preguntas.length + 1,
    archivo: null,
    ruta_archivo: null,
    activo: true,
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPoliticas = async () => {
    try {
      const [activas, inactivas] = await Promise.all([
        axios.get(`${API_URL}/api/documentos/preguntas`, {
          params: { tipo: "certificado", solo_activos: true },
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        axios.get(`${API_URL}/api/documentos/preguntas`, {
          params: { tipo: "certificado", solo_activos: false },
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      const processCert = (cert) => ({
        ...cert,
        id: cert.id_pregunta || cert.id,
        activo: cert.activo !== false,
        requiere_archivo: cert.requiere_archivo === 1 || cert.requiere_archivo === true,
      });

      return {
        activas: activas.data.sort((a, b) => a.orden - b.orden).map(processCert),
        inactivas: inactivas.data.filter((preg) => !preg.activo).sort((a, b) => a.orden - b.orden).map(processCert),
      };
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const { activas, inactivas } = await fetchPoliticas();
        setPreguntas(activas.length ? activas : [createNewRow()]);
        setPreguntasInactivas(inactivas);
      } catch (error) {
        setError("Error al cargar las políticas");
        setPreguntas([createNewRow()]);
        setPreguntasInactivas([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log("Políticas a guardar:", preguntas.map(p => ({
        id: p.id,
        titulo: p.titulo,
        requiere_archivo: p.requiere_archivo
      })));

      const hasEmptyFields = preguntas.some((cert) => {
        if (!cert.titulo) return true;
        if (!cert.tipo_persona) {
          setError(`Debe seleccionar un tipo de persona para "${cert.titulo || "política sin título"}"`);
          return true;
        }
        if (!cert.archivo && !cert.ruta_archivo) {
          setError(`Debe subir un archivo para la política "${cert.titulo || "política sin título"}"`);
          return true;
        }
        if (cert.archivo instanceof File && cert.archivo.type !== "application/pdf") {
          setError(`El archivo para "${cert.titulo || "política sin título"}" debe ser PDF`);
          return true;
        }
        return false;
      });

      if (hasEmptyFields) {
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem("token");
      const savePromises = preguntas.map(async (cert, index) => {
        const formData = new FormData();
        formData.append("titulo", cert.titulo);
        formData.append("descripcion", cert.descripcion || "");
        formData.append("tipo_persona", cert.tipo_persona);
        formData.append("tipo", "certificado");
        formData.append("obligatorio", cert.obligatorio ? "true" : "false");
        formData.append("requiere_archivo", cert.requiere_archivo ? "true" : "false");
        formData.append("orden", (index + 1).toString());
        formData.append("activo", "true");

        console.log(`Enviando política ${cert.titulo}: requiere_archivo = ${cert.requiere_archivo}`);

        if (cert.archivo instanceof File) {
          formData.append("archivo", cert.archivo);
        }

        const config = {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
        };

        if (cert.id && !cert.id.toString().startsWith("new-")) {
          return axios.put(`${API_URL}/api/documentos/preguntas/${cert.id}`, formData, config);
        } else {
          return axios.post(`${API_URL}/api/documentos/preguntas`, formData, config);
        }
      });

      const responses = await Promise.all(savePromises);
      console.log("Respuestas del servidor:", responses.map(r => r.data));

      const { activas, inactivas } = await fetchPoliticas();
      setPreguntas(activas.length ? activas : [createNewRow()]);
      setPreguntasInactivas(inactivas);
      showSnackbar("Políticas guardadas correctamente", "success");
    } catch (error) {
      console.error("Error en handleSave:", error);
      setError(error.response?.data?.detail || error.response?.data?.error || error.message || "Error al guardar las políticas");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleActivo = async (id_pregunta, activar) => {
    try {
      if (!id_pregunta || id_pregunta.toString().startsWith("new-")) {
        if (activar) return;
        setPreguntas(preguntas.filter((p) => p.id !== id_pregunta));
        return;
      }

      const config = {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      };

      const endpoint = activar
        ? `${API_URL}/api/documentos/preguntas/${id_pregunta}/activar`
        : `${API_URL}/api/documentos/preguntas/${id_pregunta}`;

      const method = activar ? "PUT" : "DELETE";

      await axios({ method, url: endpoint, ...config });

      const { activas, inactivas } = await fetchPoliticas();
      setPreguntas(activas.length ? activas : [createNewRow()]);
      setPreguntasInactivas(inactivas);
      showSnackbar(`Política ${activar ? "activada" : "desactivada"} correctamente`, "success");
    } catch (error) {
      setError(`Error al ${activar ? "activar" : "desactivar"} política`);
    }
  };

  const handleFileChange = (politicaIndex, file) => {
    const updated = [...preguntas];
    updated[politicaIndex]["archivo"] = file;
    if (file) {
      updated[politicaIndex]["ruta_archivo"] = null;
    }
    setPreguntas(updated);
  };

  const handlePreviewFile = (politica) => {
    const pdfUrl = getFilePreviewUrl(politica);
    const fileName = getFileName(politica);
    const downloadUrl = getDownloadUrl(politica);

    setPdfPreviewData({
      url: pdfUrl,
      fileName: fileName,
      downloadUrl: downloadUrl,
      rutaArchivo: politica.ruta_archivo
    });
    setShowPdfModal(true);
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    if (pdfPreviewData.url && pdfPreviewData.url.startsWith("blob:")) {
      URL.revokeObjectURL(pdfPreviewData.url);
    }
  };

  const updatePolitica = useCallback((index, field, value) => {
    console.log(`Actualizando política en índice ${index}, campo ${field} = ${value}`);
    setPreguntas(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      console.log("Nuevo estado de preguntas:", updated.map(p => ({ titulo: p.titulo, requiere_archivo: p.requiere_archivo })));
      return updated;
    });
  }, []);

  const filtered = preguntas.filter((p) => {
    const coincideTipo = tipoPersonaFiltro === "todos" ? true : p.tipo_persona === tipoPersonaFiltro;
    const coincideTitulo = tituloFiltro
      ? p.titulo?.toLowerCase().includes(tituloFiltro.toLowerCase())
      : true;
    const coincideRequiereArchivo = requiereArchivoFiltro === "todos" ? true : p.requiere_archivo === (requiereArchivoFiltro === "si");
    return coincideTipo && coincideTitulo && coincideRequiereArchivo;
  });

  if (isLoading) return (
    <Box display="flex" justifyContent="center" mt={4}>
      <CircularProgress />
    </Box>
  );

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4 align-items-center">
        <div className="col text-center text-md-start">
          <h2 className="h3">
            <i className="bi bi-shield-check me-2"></i>
            Políticas
          </h2>
          <p className="text-muted mb-0">
            Configura las políticas que deben aceptar los proveedores
          </p>
        </div>
      </div>

      <hr />

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">Buscar por título:</label>
              <input
                type="text"
                className="form-control"
                placeholder="Escriba el título..."
                value={tituloFiltro}
                onChange={(e) => setTituloFiltro(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Filtrar por tipo de persona:</label>
              <select
                className="form-select"
                value={tipoPersonaFiltro}
                onChange={(e) => setTipoPersonaFiltro(e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="natural">Persona Natural</option>
                <option value="juridica">Persona Jurídica</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Requiere archivo:</label>
              <select
                className="form-select"
                value={requiereArchivoFiltro}
                onChange={(e) => setRequiereArchivoFiltro(e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="si">Sí</option>
                <option value="no">No</option>
              </select>
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label fw-semibold">&nbsp;</label>
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setTituloFiltro("");
                  setTipoPersonaFiltro("todos");
                  setRequiereArchivoFiltro("todos");
                }}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="d-none d-lg-block">
        <DynamicTable
          data={filtered}
          setData={setPreguntas}
          onSave={handleSave}
          showButtons={true}
          editable={true}
          tipo="certificado"
          tipoPersonaFiltro={tipoPersonaFiltro}
          onToggleActivo={(id) => toggleActivo(id, false)}
          tipoPersonaOptions={[
            { value: "", label: "Seleccionar..." },
            { value: "natural", label: "Persona Natural" },
            { value: "juridica", label: "Persona Jurídica" },
          ]}
        />
      </div>

      <div className="d-block d-lg-none">
        {filtered.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-inbox display-4 d-block mb-2"></i>
            No hay políticas activas que coincidan con los filtros
          </div>
        ) : (
          <div>
            {filtered.map((politica) => {
              const realIndex = preguntas.findIndex(p => p.id === politica.id);
              return (
                <PoliticaCard
                  key={politica.id}
                  politica={politica}
                  index={realIndex}
                  updatePolitica={updatePolitica}
                  preguntas={preguntas}
                  setPreguntas={setPreguntas}
                  handleFileChange={handleFileChange}
                  handlePreviewFile={handlePreviewFile}
                  toggleActivo={toggleActivo}
                />
              );
            })}

            <div className="mt-4">
              <button
                className="btn btn-success w-100"
                onClick={handleSave}
                disabled={preguntas.some(
                  (item) =>
                    !item.titulo ||
                    (!item.archivo && !item.ruta_archivo)
                )}
              >
                <i className="bi bi-save me-2"></i>
                Guardar Cambios
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        open={showPdfModal}
        onClose={handleClosePdfModal}
        aria-labelledby="pdf-preview-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          height: '80vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <i className="bi bi-file-earmark-pdf text-danger me-2"></i>
              <span>{pdfPreviewData.fileName || "Vista previa del PDF"}</span>
            </Box>
            <Button onClick={handleClosePdfModal} size="small">
              Cerrar
            </Button>
          </Box>
          <Box sx={{ flex: 1, p: 0, overflow: 'auto' }}>
            {pdfPreviewData.url ? (
              <iframe
                src={pdfPreviewData.url}
                title="PDF Preview"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <i className="bi bi-file-earmark-x" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
                <p className="mt-3 text-muted">No hay archivo para previsualizar</p>
              </Box>
            )}
          </Box>
          {pdfPreviewData.url && (
            <Box sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1
            }}>
              <Button
                variant="outlined"
                href={pdfPreviewData.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<i className="bi bi-box-arrow-up-right"></i>}
              >
                Abrir en nueva pestaña
              </Button>
              {pdfPreviewData.downloadUrl && (
                <Button
                  variant="contained"
                  href={pdfPreviewData.downloadUrl}
                  download
                  startIcon={<i className="bi bi-download"></i>}
                >
                  Descargar
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default Politicas;